import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'
import 'leaflet/dist/leaflet.css'
import React, { PropsWithChildren, useMemo } from 'react'
import { MapStyled } from './Map.style'
import { MapContainer } from 'react-leaflet'
import { MapHeader, MapMarker, MapMarkerProps } from './components/ui'
import { MapUtils } from './components/util'
import { useMapContext } from './MapProvider'
import { FilterSection } from './components/ui/MapFilters/FilterSection'
import { PolygonInternal } from './components/ui/Polygon'
import { KmlLayer } from './components/ui/KML'
import MarkerClusterControl from './components/ui/MarkerClusterControl'
import { Polylines } from './components/ui/Polyline'
import { useSelector } from 'react-redux'
import { selectTileProvider } from 'services/slices/auth'
import { MarkersWithSuperCluster } from 'components/MapV2/components/ui/MarkersWithSuperCluster'

const DEFUALT_ZOOM = 16

export const MapInternal: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    zoom,
    markers,
    center,
    bounds,
    scrollWheelZoom,
    location,
    customFullscreen,
    columns,
    onFetch,
    isFilterOpen,
    polygons,
    kmls,
    border,
    polylines,
    noCluster,
    'data-testid': dataTestId,
    supercluster,
    selectedMarkersSet,
  } = useMapContext()

  const tileProvider = useSelector(selectTileProvider)

  const markerGroup: { clusterable: Array<MapMarkerProps>; nonClusterable: Array<MapMarkerProps> } =
    useMemo(() => {
      if (noCluster) {
        return { clusterable: [], nonClusterable: markers ?? [] }
      }
      const clusterableMarkers: Array<MapMarkerProps> = []
      const nonClusterableMarkers: Array<MapMarkerProps> = []

      return markers
        ? markers.reduce(
            (acc, marker) => {
              if (marker.clusterable && !selectedMarkersSet.has(marker.id)) {
                acc.clusterable.push(marker)
              } else {
                acc.nonClusterable.push(marker)
              }
              return acc
            },
            {
              clusterable: clusterableMarkers,
              nonClusterable: nonClusterableMarkers,
            },
          )
        : { clusterable: clusterableMarkers, nonClusterable: nonClusterableMarkers }
    }, [markers, selectedMarkersSet, noCluster])

  return (
    <MapStyled border={border} data-testid={dataTestId}>
      <MapHeader />
      <MapContainer
        worldCopyJump // This is for the map to be continuous
        zoomControl={false} // We are using custom zoom control
        fullscreenControl={!customFullscreen}
        maxZoom={tileProvider === 'openstreetmap' ? 18 : 20}
        scrollWheelZoom={scrollWheelZoom}
        bounds={bounds}
        center={center ?? location ?? bounds ? undefined : [0, 0]}
        zoom={zoom ?? DEFUALT_ZOOM}
      >
        <MapUtils />

        {supercluster ? (
          <MarkersWithSuperCluster data={markerGroup.clusterable} />
        ) : (
          <MarkerClusterControl
            showCoverageOnHover={false}
            maxClusterRadius={40}
            removeOutsideVisibleBounds={false}
          >
            {markerGroup.clusterable.map((marker, index) => (
              <MapMarker key={index} {...marker} />
            ))}
          </MarkerClusterControl>
        )}

        {markerGroup.nonClusterable.map((marker, index) => (
          <MapMarker key={index} {...marker} />
        ))}

        {polygons?.map((polygon, index) => (
          <PolygonInternal key={index} polygon={polygon} pmIgnore={true} />
        ))}
        {polylines && <Polylines polylines={polylines} />}
        {kmls && <KmlLayer kmls={kmls} />}
        {children}
        {columns && onFetch && isFilterOpen && (
          <FilterSection columns={columns} onFetch={onFetch} />
        )}
      </MapContainer>
    </MapStyled>
  )
}
